/* ═══════════════════════════════════════════════════════════════════
   Wave Frozen Record — "Why these creators?" (B3, 2026-06)

   Renders GET /api/brand/waves/{id}/frozen-record: the exact decisions
   the engine froze at the moment this wave was sent. Per-creator match
   score + components, role, burst, scheduled slot, plus the arrangement
   summary. Promise 1 ("we can prove the match was good") made visible —
   the embryo of the Wave Report.

   Copy discipline: same banned-words rule as dynamics_narration — no
   internal stack vocabulary leaks to brands. NO lift or revenue
   projections anywhere on this page (exposure ruling).
   ═══════════════════════════════════════════════════════════════════ */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ShieldCheck, Loader2, Users, Hash, Layers, Clock, AlertTriangle,
} from 'lucide-react';
import DashboardLayout from '../layouts/DashboardLayout';

const API = process.env.REACT_APP_BACKEND_URL;
const CARD = '#121212';
const BORDER = '#1E1E1E';
const T = '#00F5FF';
const P = '#FF6BBD';

// Plain-English role labels — brand sees story, not stack.
const ROLE_LABELS = {
  patient_zero:       'Lead voice — fires first to seed momentum',
  amplifier:          'Amplifier — carries the wave to new audiences',
  engagement_prompter: 'Conversation starter — drives comments and replies',
};

const COMPONENT_LABELS = [
  { key: 'rule_component',     label: 'Category fit' },
  { key: 'semantic_component', label: 'Topic fit' },
  { key: 'reach_component',    label: 'Reach' },
];

function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function ComponentBar({ label, value, weight }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="flex items-center gap-2">
      <span className="text-[9px] uppercase tracking-wider text-gray-500 font-bold w-20 shrink-0">{label}</span>
      <div className="flex-1 h-1.5 rounded-full" style={{ background: '#0A0A0A' }}>
        <div className="h-full rounded-full" style={{ width: `${v}%`, background: T }} />
      </div>
      <span className="text-[10px] font-bold text-white w-9 text-right">{Math.round(v)}</span>
      {weight != null && (
        <span className="text-[8px] text-gray-600 w-12 text-right">×{Number(weight).toFixed(2)}</span>
      )}
    </div>
  );
}

function CreatorCard({ c }) {
  const comps = c.match_components || {};
  const weights = comps.weights_used || [];
  const baselines = c.baselines_at_snapshot || {};
  return (
    <div className="rounded-xl p-4 space-y-3" style={{ background: CARD, border: `1px solid ${BORDER}` }}
         data-testid={`frozen-creator-${(c.ssp_authoritative_handle || c.handle || '').replace('@', '')}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-bold text-white truncate">{c.ssp_authoritative_handle || c.handle}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">
            {ROLE_LABELS[c.role_hint] || 'Cohort member'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[8px] uppercase tracking-widest text-gray-500 font-bold">Match score at send</p>
          <p className="text-2xl font-black" style={{ color: T }} data-testid="frozen-match-score">
            {Math.round(Number(c.match_score) || 0)}
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        {COMPONENT_LABELS.map(({ key, label }, i) => (
          <ComponentBar key={key} label={label} value={comps[key]} weight={weights[i]} />
        ))}
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-1 pt-1" style={{ borderTop: `1px solid ${BORDER}` }}>
        <Meta label="Burst" value={c.burst_number != null ? `#${c.burst_number}` : '—'} />
        <Meta label="Scheduled" value={fmtTime(c.scheduled_post_time)} />
        <Meta label="Platform" value={c.platform || '—'} />
        <Meta label="Followers at send" value={baselines.followers != null ? Number(baselines.followers).toLocaleString() : '—'} />
        <Meta label="Engagement at send" value={baselines.engagement_rate != null ? `${(Number(baselines.engagement_rate) * 100).toFixed(1)}%` : '—'} />
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-[8px] uppercase tracking-widest text-gray-600 font-bold">{label}</p>
      <p className="text-[11px] font-semibold text-gray-300 mt-0.5">{value}</p>
    </div>
  );
}

function EmptyState({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl p-10 text-center" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
      <Icon className="h-8 w-8 mx-auto mb-3 text-gray-600" />
      <p className="text-sm font-bold text-white">{title}</p>
      <p className="text-xs text-gray-500 mt-2 max-w-md mx-auto leading-relaxed">{body}</p>
    </div>
  );
}

export default function WaveFrozenRecordPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/api/brand/waves/${id}/frozen-record`, { withCredentials: true });
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(e.response?.status === 404 ? 'Wave not found.' : 'Could not load the wave record.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const frozen = data?.frozen_at_send;
  const arrangement = frozen?.arrangement || {};

  return (
    <DashboardLayout>
      <div className="max-w-[1000px] mx-auto px-5 lg:px-8 py-6" data-testid="wave-frozen-record-page">
        <Link to={`/waves/${id}/control`} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-gray-500 hover:text-white transition-colors font-bold" data-testid="frozen-back-to-control">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Control Room
        </Link>

        <header className="mt-5 mb-7">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="h-3.5 w-3.5" style={{ color: T }} />
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: T }}>Why these creators?</p>
          </div>
          <h1 className="text-2xl font-black text-white tracking-tight" data-testid="frozen-record-title">
            {data?.goal || 'Wave record'}
          </h1>
          <p className="text-[12px] text-gray-400 leading-relaxed mt-2 max-w-2xl">
            This is the permanent record of what the engine decided the moment this wave was sent —
            every score, role and time slot, exactly as computed. Nothing here is recalculated after the fact.
          </p>
        </header>

        {loading && (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin" style={{ color: T }} /></div>
        )}
        {error && !loading && (
          <EmptyState icon={AlertTriangle} title={error} body="Check the wave link, or head back to Campaigns." />
        )}

        {!loading && !error && data && !frozen && (
          data.legacy_pre_capture ? (
            <EmptyState
              icon={Clock}
              title="Sent before record-keeping began"
              body="This wave fired before the decision-record system shipped, so no frozen record exists for it. Every wave sent since then carries one."
            />
          ) : (
            <EmptyState
              icon={Clock}
              title="This wave hasn't been sent yet"
              body="The engine freezes its decision record at the moment you press Send. Fire the wave and this page will fill in."
            />
          )
        )}

        {!loading && frozen && (
          <>
            {/* Arrangement summary */}
            <div className="rounded-xl p-4 mb-6 flex flex-wrap gap-x-8 gap-y-3" style={{ background: CARD, border: `1px solid ${T}25` }} data-testid="frozen-arrangement-summary">
              <Meta label="Sent" value={fmtTime(data.sent_at)} />
              <div>
                <p className="text-[8px] uppercase tracking-widest text-gray-600 font-bold flex items-center gap-1"><Hash className="h-2.5 w-2.5" />Wave hashtag</p>
                <p className="text-[11px] font-semibold mt-0.5" style={{ color: P }}>{data.unique_hook || '—'}</p>
              </div>
              <div>
                <p className="text-[8px] uppercase tracking-widest text-gray-600 font-bold flex items-center gap-1"><Layers className="h-2.5 w-2.5" />Bursts</p>
                <p className="text-[11px] font-semibold text-gray-300 mt-0.5">{data.total_bursts ?? arrangement.total_bursts ?? '—'}</p>
              </div>
              <div>
                <p className="text-[8px] uppercase tracking-widest text-gray-600 font-bold flex items-center gap-1"><Users className="h-2.5 w-2.5" />Creators</p>
                <p className="text-[11px] font-semibold text-gray-300 mt-0.5">
                  {frozen.creators_resolved ?? (frozen.creators || []).length} confirmed
                  {frozen.creators_skipped ? ` · ${frozen.creators_skipped} skipped` : ''}
                </p>
              </div>
              <Meta label="Engine version" value={frozen.engine_version || '—'} />
            </div>

            {/* Per-creator decision records */}
            <div className="space-y-3" data-testid="frozen-creator-list">
              {(frozen.creators || []).map((c, i) => (
                <CreatorCard key={c.creator_account_id || c.handle || i} c={c} />
              ))}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
